import { Component, inject, Signal, OnInit } from '@angular/core';
import {
  desktopMenuOptions,
  NavigationOption,
  ParamOptions,
} from './navgation.options';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon'; // Add this import
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterOutlet,
  NavigationEnd,
} from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { RetailXProComponent } from '../retail-x-pro/retail-x-pro.component';
import { map, filter } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { LoaderService } from 'src/app/services/loader/loader.service';
import {faDatabase } from '@fortawesome/free-solid-svg-icons';
import {IconProp} from '@fortawesome/fontawesome-svg-core';
import { SettingsService } from 'src/app/services/settings/settings.service';
import { environment } from 'src/environments/environment';
import { RxAuthService } from 'src/app/services/rx-auth/rx-auth.service';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  imports: [
    MatSidenavModule,
    MatListModule,
    MatIconModule, // Add this line
    RouterLink,
    CommonModule,
    FontAwesomeModule,
    RetailXProComponent,
    RouterOutlet,
    FormsModule,
    MatSlideToggleModule,
    MatTooltipModule
  ],
  standalone: true,
})
export class SidebarComponent implements OnInit {
  showSidebar: boolean = true;
  public dataIcon = faDatabase as IconProp;
  private loaderService: LoaderService = inject(LoaderService);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);

  public navlist: NavigationOption[] = desktopMenuOptions;

  public active: Signal<string>;
  public currentRoute: Signal<string>;
  public currentParams: Signal<Record<string, any>>;

  public isSwitchOn: boolean = false;

  constructor(
    private settingsService: SettingsService,
    public auth: RxAuthService
  ) {
    // Initialize the switch state from the service
    this.isSwitchOn = this.settingsService.getEditMode();
    // Initialize signals here
    this.active = toSignal(
      this.router.events.pipe(
        map(() => {
          const childRoute = this.getChildRoute(this.activatedRoute);
          return childRoute.snapshot.data['sidebarTab'];
        })
      ),
      { initialValue: 'dashboard' }
    );

    this.currentRoute = toSignal(
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          const childRoute = this.getChildRoute(this.activatedRoute);
          return this.getCurrentUrl(childRoute);
        })
      ),
      { initialValue: 'landing' }
    );

    this.currentParams = toSignal(
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          const childRoute = this.getChildRoute(this.activatedRoute);
          return this.getCurrentParams(childRoute);
        })
      ),
      { initialValue: {} }
    );
  }

  private getChildRoute(route: ActivatedRoute): ActivatedRoute {
    this.loaderService.setIsLoading(false);

    let child = route.firstChild;
    while (child && child.firstChild) {
      child = child.firstChild;
    }
    return child || route;
  }

  private getCurrentParams(route: ActivatedRoute): Record<string, any> {
    const params = { ...route.snapshot.params };
    const queryParams = {
      ...this.router.routerState.root.snapshot.queryParams,
    };

    return { ...params, ...queryParams };
  }

  private getCurrentUrl(route: ActivatedRoute): string {
    return route.snapshot.url.map((segment) => segment.path).join('/') || '/';
  }

  public isCurrentRoute(navLink: string, navParams: any): boolean {
    const path = navLink.split('/')[1];
    const currentRoute = this.currentRoute();
    const currentParams = this.currentParams();

    if (path === 'dashboard' && currentRoute === 'landing') {
      return true;
    } else if (path === 'explore' && currentRoute === 'explore') {
      if (currentParams['search'] === '' && navParams.view) {
        return currentParams['view'] === navParams.view;
      }
    } else {
      return currentRoute === path;
    }

    return false;
  }

  public navigate(option: NavigationOption): void {
    if (option.externalUrl) {
      window.location.href = option.externalUrl;
    } else if (option.route) {
      const params = option.params ? this.serializeParams(option.params) : {};
      this.router.navigate([option.route], { queryParams: params });
    }
  }

  public serializeParams(params: ParamOptions): { [key: string]: any } {
    const paramObject: { [key: string]: any } = {};

    if (params.search !== undefined) paramObject['search'] = params.search;
    if (params.view !== undefined) paramObject['view'] = params.view;
    if (params.id !== undefined) paramObject['id'] = params.id;

    return paramObject;
  }

  ngOnInit(): void {
    // Log initial values to ensure they're set correctly
    this.currentRoute();
    this.currentParams();
  }

  redirectToUpgrade(): void {
    window.location.href = `${environment.wp.sign_up_url}`;
  }

  onSwitchChange(event: boolean): void {
    this.isSwitchOn = event;
    this.settingsService.setEditMode(this.isSwitchOn); // Update the service
  }
}
